import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_LPCO_INFO_R,
    SG_LPCO_PRCS_LIST_U,
    PRCS_DESC,
    GRID_DEFAULT_DATA,
    withAprvColumn,
} from "./services/OsciCfgnPrcsService";
import { CommonErrors } from "@/comn/components";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * Modify LPCO Process
 * !== LPCO Process 수정 ==!
 */
export const OSCI0203012S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { prcsMnId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    const [appPrcsList, setAppPrcsList] = useState(comnUtils.getGridData([]));
    const [appTpCd, setAppTpCd] = useState("");

    // ORG LIST
    const [orgCdList, setOrgCdList] = useState([]);
    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);
    // Duplicate-allowed process codes !== 중복 허용 단계 ==!
    const [dupAlwList, setDupAlwList] = useState<string[]>([]);
    // Approval-configurable process codes !== 결재 설정 가능 단계 ==!
    const [aprvAblList, setAprvAblList] = useState<string[]>([]);

    // original data
    const refOriginData = useRef({ info: {}, prcsList: Array() });
    const refDeletedData = useRef<any[]>([]);

    const { getResource } = useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0116" },
            { area: "comnCd", comnCd: "OSC0120" },
            { area: "comnCd", comnCd: "OSC0113" },
            { area: "comnCd", comnCd: "OSC0143" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Register LPCO Component !== LPCO Component 등록 ==!
        lpcoInfoRgsr: useForm({
            defaultSchema: SF_LPCO_INFO_R,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Component List !== Component 목록 ==!
        appPrcsList: useGrid({
            defaultSchema: SG_LPCO_PRCS_LIST_U,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_appPrcsList: {
            head: {},
            cell: {},
            edit: {
                prcsRoleTpCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    let arrExcludes: any[] = [];
                    if (value != "SU" && value != "AP") {
                        arrExcludes = ["SU", "AP"];
                    }
                    if (appTpCd != "EA") {
                        arrExcludes = arrExcludes.concat(["EV", "EF"]);
                    }
                    return (
                        <FormControl
                            type="select"
                            area="comnCd"
                            comnCd="OSC0120"
                            value={rowValues.prcsRoleTpCd}
                            edit={!(value === "SU" || value === "AP")}
                            viewType={value === "SU" || value === "AP" ? "label" : "both"}
                            excludes={arrExcludes}
                            onChange={(v) => {
                                let orgCd = form.lpcoInfoRgsr.getValue("orgCd");
                                if (v == "EV" || v == "EF") {
                                    orgCd = lpcoEnvs.ORG_CD_TRA;
                                }
                                grid.appPrcsList.updateRow({
                                    ...rowValues,
                                    prcsRoleTpCd: v,
                                    prcsRoleDesc: PRCS_DESC[v],
                                    orgCd: orgCd,
                                    aprvExisYn: aprvAblList.includes(v) ? rowValues.aprvExisYn : "N",
                                });
                            }}
                        />
                    );
                },
                orgCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    const prcsRoleTpCd = rowValues.prcsRoleTpCd;
                    let orgCd = rowValues.orgCd;
                    if (!orgCd && prcsRoleTpCd != "SU" && prcsRoleTpCd != "AP") {
                        orgCd = form.lpcoInfoRgsr.getValue("orgCd");
                        grid.appPrcsList.updateRow({
                            ...rowValues,
                            orgCd: orgCd,
                        });
                    }

                    return (
                        <FormControl
                            type="select"
                            value={orgCd}
                            options={[...orgCdList]}
                            edit={true}
                            viewType="label"
                            onChange={(v) => {
                                grid.appPrcsList.updateRow({
                                    ...rowValues,
                                    orgCd: v,
                                });
                            }}
                        />
                    );
                },
                aprvExisYn: (props: any) => {
                    const { rowValues, value } = props;
                    // Editable only for approval-configurable stages !== 결재 설정 가능 단계만 편집 ==!
                    const edit = aprvAblList.includes(rowValues.prcsRoleTpCd);
                    return (
                        <FormControl
                            type="select"
                            area="comnCd"
                            comnCd="OSC0101"
                            value={edit ? value : "N"}
                            edit={edit}
                            viewType={edit ? "both" : "label"}
                            onChange={(v) => {
                                grid.appPrcsList.updateRow({
                                    ...rowValues,
                                    aprvExisYn: v,
                                });
                            }}
                        />
                    );
                },
            },
            row: (row: any, context: any) => {
                // row : 해당 row 의 데이터(rowValues)
                // return 값이 true 이면 row 보임
                // return 값이 false 이면 row 숨김
                // context 의 속성을 변경하여 row 의 바탕색 변경 가능
                if (row.prcsRoleTpCd === "SU" || row.prcsRoleTpCd === "AP") {
                    context.backgroundColor = "blue"; // 3가지 색상 옵션 red , yellow , blue
                }
                return true;
            },
            checkbox: (row: any) => {
                // row : 해당 row 의 데이터(rowValues)
                return !(row.prcsRoleTpCd === "SU" || row.prcsRoleTpCd === "AP");
            },
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        /* Register LPCO Process !== LPCO Process 등록 ==! */
        saveAppPrcs: useFetch({
            api: (prcsMnId, data) => APIS.saveAppPrcs(prcsMnId, { ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        navigate(URLS.osci0203011s + "/" + prcsMnId);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203011s + "/" + prcsMnId);
                    },
                });
            },
            onError: (error) => {
                modal.openModal({
                    content: error.response.data.errorMessage,
                });
            },
        }),
        /* Process configuration parameters !== 프로세스 설정 파라미터 조회 ==! */
        getPrcsPrmt: useFetch({
            api: () => APIS.getPrcsPrmt(),
            onSuccess: (data) => {
                const prmt = data?.prmt?.content || {};
                setDupAlwList(prmt.dupAlwList || []);
                setAprvAblList(prmt.aprvAblList || []);
                // Show the approval column only when configurable stages exist !== 결재 설정 가능 단계가 있을 때만 컬럼 표시 ==!
                if ((prmt.aprvAblList || []).length > 0) {
                    grid.appPrcsList.setSchema(withAprvColumn(SG_LPCO_PRCS_LIST_U, true));
                }
            },
            onError: () => {},
            showToast: false,
        }),
        getAppDtl: useFetch({
            api: (prcsMnId) => {
                return APIS.getAppDtl(prcsMnId);
            },
            onSuccess: (data) => {
                console.log("getAppDtl onSuccess ==================== ", data);
                const info = data?.appInfo?.content?.info || {};
                form.lpcoInfoRgsr.setValues(info);

                setAppTpCd(info.appTpCd);

                let prcsList = data.appInfo.content?.prcsList;
                if (!prcsList || prcsList.length == 0) prcsList = [...GRID_DEFAULT_DATA];
                setAppPrcsList(comnUtils.getGridData(prcsList));

                refOriginData.current = { info: { ...data.appInfo.content?.info }, prcsList: [...prcsList] };
            },
            onError: () => {},
            showToast: false,
        }),
    };
    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Save Process !== Process 저장 ==!
        saveAppPrcs: form.lpcoInfoRgsr.handleSubmit(async () => {
            let appPrcsList = grid.appPrcsList.getViewData() || [];

            // const processCodeList = appPrcsList.filter(
            //     (item) => item.prcsRoleTpCd != "SU" && item.prcsRoleTpCd != "AP",
            // );

            if (!appPrcsList || appPrcsList.length == 0) {
                // Please enter one or more processing processes. !== 처리 프로세스를 하나 이상 입력하세요.==!
                modal.openModal({
                    content: "msg.osc.00019",
                });
            } else {
                // grid validate
                let errResult = grid.appPrcsList.validate() || {};
                errResult["errors"] = errResult?.errors || [];

                const duplMap: { [key: string]: boolean } = {};
                let idx = 1;
                for (const item of appPrcsList) {
                    const val = item.prcsRoleTpCd;
                    if (val && duplMap[val] && !dupAlwList.includes(val)) {
                        const strLabel = await lpcoUtils.getCodeLabel(getResource("comnCd", "OSC0120"), val);
                        // const strLabel = await lpcoUtils.getCodeLabel("OSC0120", val);
                        errResult.errors.push(lpcoUtils.getErrorObj(idx, "L_PRCS_CFGN_CD", "msg.osc.00017", strLabel));
                    } else {
                        duplMap[val] = true;
                    }
                    idx++;
                }

                // grid validate

                if (errResult && errResult.errors?.length > 0) {
                    // sort by row
                    errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                    // error layer popup
                    modal.openModal({
                        content: <CommonErrors {...errResult} />,
                        size: "lg",
                        draggable: true,
                    });
                } else {
                    // if (refDeletedData.current.length > 0) {
                    //     appPrcsList = appPrcsList.concat(refDeletedData.current);
                    // }

                    const data = { ...form.lpcoInfoRgsr.getValues(), prcsList: grid.appPrcsList.getData() || [] };
                    modal.openModal({
                        content: "msg.osc.00090" /**Do you want to save ? */,
                        onConfirm: () => {
                            fetch.saveAppPrcs.fetch(prcsMnId, data);
                        },
                    });
                }
            }
        }),

        click_Btn_AddGridList: () => {
            form.lpcoInfoRgsr.handleSubmit(() => {
                grid.appPrcsList.addRow();
            })();
        },
        click_Btn_DeleteGridList: () => {
            const seltList: any[] = grid.appPrcsList.getSelectedRow() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }
            grid.appPrcsList.deleteRow("radio");
        },
        refreshOrgCdAllList: async () => {
            setOrgCdList((await lpcoUtils.getOrgCdAllList()) || []);
        },
        refreshOrgLpcoList: async (usrOrgCd: any) => {
            console.log("refreshOrgLpcoList");
            if (usrOrgCd) setOrgLpcoList((await lpcoUtils.getOrgLpcoByPrcsList(usrOrgCd)) || []);
            else setOrgLpcoList([]);
        },
        setAppDtl: async (prcsMnId: any) => {
            fetch.getAppDtl.fetch(prcsMnId);
        },
    };

    useEffect(() => {
        fetch.getPrcsPrmt.fetch(); // process configuration parameters !== 프로세스 설정 파라미터 ==!

        handler.refreshOrgCdAllList();

        handler.setAppDtl(prcsMnId); // lpco app info
    }, []);

    return (
        <Page
        // id={pgeUid}
        // title={t("L_EDIT_FM_FLD")}
        // navigation={{
        //     base: comnEnvs.base,
        //     nodes: [...BASE.nodes, { path: "/eswo/lpco/cfgn/osci0203001s", label: "L_EDIT_FM_FLD" }],
        // }}
        >
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO_APP_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.orgNm} options={orgCdList}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoInfoRgsr.schema.appNm}
                                options={orgLpcoList}
                                onChange={(ev: any) => {
                                    console.log(ev);
                                }}
                            ></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.lpcoAplyTpCd} edit={true}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appdDt} edit={true}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_PRCS"} titleSize={2}></Group.Title>
                    <Layout direction="row">
                        <Layout.Left></Layout.Left>
                        <Layout.Right>
                            <Button
                                icon="up"
                                onClick={() => {
                                    // 선택된 그리드 Row를 위쪽으로 이동
                                    grid.appPrcsList.upRow(grid.appPrcsList.getSelectedRow());
                                }}
                            ></Button>
                            <Button
                                icon="down"
                                onClick={() => {
                                    // 선택된 그리드 Row를 아래쪽으로 이동
                                    grid.appPrcsList.downRow(grid.appPrcsList.getSelectedRow());
                                }}
                            ></Button>

                            <Button
                                role="gridAdd"
                                onClick={() => {
                                    handler.click_Btn_AddGridList();
                                }}
                            ></Button>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.click_Btn_DeleteGridList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid
                        {...grid.appPrcsList.grid}
                        data={appPrcsList} /* fetch.getRpckItmAppList.data?.appPrcsList */
                        render={render.grid_appPrcsList}
                        // onCellClick={handler.click_Grid_RpckItmAppList}
                        // onRowClick={handler.click_GridRow_RpckItmAppList}
                    />
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203010q);
                        }}
                    ></Button>
                    <Button
                        role="reset"
                        onClick={() => {
                            //form.lpcoInfoRgsr.reset({ ...orginDataRef.current.info });
                            //grid.appPrcsList.resetDta();
                            //grid.appPrcsList.setData(comnUtils.getGridData(refOriginData.current.prcsList));
                            console.log("refOriginData.current.prcsList", refOriginData.current.prcsList);
                            setAppPrcsList(comnUtils.getGridData(refOriginData.current.prcsList));
                            refDeletedData.current = [];
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.saveAppPrcs}></Button>
                    <Button role="cancel" onClick={() => navigate(`${URLS.osci0203011s}/${prcsMnId}`)}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
